import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useSalonContextStore } from '../../store/salonContextStore';
import { salonApi, WorkingHours } from '../../services/salonService';
import { serviceApi, Service } from '../../services/serviceService';
import { branchService, SalonBrand } from '../../services/inventoryService';
import {
  PageHeader,
  SectionCard,
  EmptyState,
  LoadingState,
  StatusPill,
  FormSection,
  FieldGrid,
  SaveBar,
  useUnsavedChangesWarning,
  useConfirm,
} from '../../components/ui';
import { tokens } from '../../theme';
import { formatMoney, formatDuration, plural, countLabel } from '../../utils/format';

interface PublicProfileForm {
  slug: string;
  publicTitle: string;
  publicDescription: string;
  publicPhone: string;
  publicEmail: string;
  publicAddress: string;
  allowOnlineBooking: boolean;
  allowPhoneContact: boolean;
  isPublicPublished: boolean;
}

const emptyForm: PublicProfileForm = {
  slug: '',
  publicTitle: '',
  publicDescription: '',
  publicPhone: '',
  publicEmail: '',
  publicAddress: '',
  allowOnlineBooking: true,
  allowPhoneContact: true,
  isPublicPublished: false,
};

// Pola zapisywane przyciskiem „Zapisz” (publikacja ma osobna akcje).
const SAVED_FIELDS: (keyof PublicProfileForm)[] = [
  'slug', 'publicTitle', 'publicDescription', 'publicPhone', 'publicEmail', 'publicAddress', 'allowOnlineBooking', 'allowPhoneContact',
];

const DAY_LABELS = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

/** Link w ramce z przyciskami „Kopiuj” i „Otwórz”. Dlugi adres lamie sie, zamiast rozpychac strone. */
const LinkBox: React.FC<{ url: string; onCopy: (url: string) => void }> = ({ url, onCopy }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={0.5}
    sx={{ bgcolor: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: '11px', pl: 1.75, pr: 0.75, py: 0.75, minWidth: 0 }}
  >
    <Typography
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ flex: 1, minWidth: 0, fontSize: '0.9375rem', fontWeight: 600, color: tokens.plum, textDecoration: 'none', overflowWrap: 'anywhere', '&:hover': { textDecoration: 'underline' } }}
    >
      {url.replace(/^https?:\/\//, '')}
    </Typography>
    <Tooltip title="Kopiuj link">
      <IconButton size="small" onClick={() => onCopy(url)} aria-label="Kopiuj link">
        <ContentCopyRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Otwórz w nowej karcie">
      <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" aria-label="Otwórz w nowej karcie">
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
);

export const SalonWebsitePage: React.FC = () => {
  const { activeSalonId, loadSalons } = useSalonContextStore();
  const confirm = useConfirm();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('sm'));
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<PublicProfileForm>(emptyForm);
  const [savedForm, setSavedForm] = useState<PublicProfileForm>(emptyForm);
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceSavingId, setServiceSavingId] = useState<string | null>(null);
  const [brand, setBrand] = useState<SalonBrand | null>(null);
  const [workingHours, setWorkingHours] = useState<WorkingHours[]>([]);

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  useEffect(() => {
    const load = async () => {
      if (!activeSalonId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const [salon, hours, brands] = await Promise.all([
          salonApi.getById(activeSalonId),
          salonApi.getWorkingHours(activeSalonId),
          branchService.getBrands().catch(() => [] as SalonBrand[]),
        ]);

        const loaded: PublicProfileForm = {
          slug: salon.slug || '',
          publicTitle: salon.publicTitle || '',
          publicDescription: salon.publicDescription || '',
          publicPhone: salon.publicPhone || '',
          publicEmail: salon.publicEmail || '',
          publicAddress: salon.publicAddress || '',
          allowOnlineBooking: salon.allowOnlineBooking ?? true,
          allowPhoneContact: salon.allowPhoneContact ?? true,
          isPublicPublished: salon.isPublicPublished ?? false,
        };
        setForm(loaded);
        setSavedForm(loaded);
        setWorkingHours(hours);
        setBrand(brands.find((b) => b.salons?.some((s) => s.id === activeSalonId)) || null);
      } catch {
        setError('Nie udało się załadować konfiguracji wizytówki. Odśwież stronę, aby spróbować ponownie.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activeSalonId]);

  useEffect(() => {
    const loadServices = async () => {
      if (!activeSalonId || tab !== 1) return;
      try {
        setServicesLoading(true);
        const data = await serviceApi.listBySalon(activeSalonId, true);
        setServices(data);
      } catch {
        setError('Nie udało się załadować usług.');
      } finally {
        setServicesLoading(false);
      }
    };
    loadServices();
  }, [activeSalonId, tab]);

  const publicUrl = useMemo(() => {
    if (!form.slug) return '';
    return `https://visitio.online/s/${form.slug}`;
  }, [form.slug]);

  const publishedServicesCount = useMemo(
    () => services.filter((s) => s.isActive && s.isPublicVisible !== false).length,
    [services],
  );

  const dirty = SAVED_FIELDS.some((k) => form[k] !== savedForm[k]);
  useUnsavedChangesWarning(dirty);

  const handleSave = async () => {
    if (!activeSalonId) return;

    setSaving(true);
    setError('');
    setSaveError('');
    setSuccess('');

    try {
      await salonApi.updatePublicProfile(activeSalonId, {
        slug: form.slug,
        publicTitle: form.publicTitle,
        publicDescription: form.publicDescription,
        publicPhone: form.publicPhone,
        publicEmail: form.publicEmail,
        publicAddress: form.publicAddress,
        allowOnlineBooking: form.allowOnlineBooking,
        allowPhoneContact: form.allowPhoneContact,
      });
      setSavedForm((prev) => ({ ...form, isPublicPublished: prev.isPublicPublished }));
      setSuccess('Konfiguracja wizytówki została zapisana.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się zapisać konfiguracji wizytówki.');
      setSaveError(e?.response?.data?.error || 'spróbuj ponownie za chwilę');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setSaveError('');
    setForm((prev) => ({ ...savedForm, isPublicPublished: prev.isPublicPublished }));
  };

  const togglePublished = async () => {
    if (!activeSalonId) return;

    if (form.isPublicPublished) {
      const ok = await confirm({
        title: 'Wycofać publikację wizytówki?',
        details: publicUrl ? publicUrl.replace(/^https?:\/\//, '') : undefined,
        description: 'Strona salonu zniknie z internetu, a klienci nie zarezerwują przez nią wizyty. Możesz ją opublikować ponownie w każdej chwili.',
        confirmLabel: 'Wycofaj publikację',
        tone: 'danger',
      });
      if (!ok) return;
    }

    setPublishing(true);
    setError('');
    setSuccess('');

    try {
      const next = !form.isPublicPublished;
      const updated = await salonApi.setPublicationStatus(activeSalonId, next);
      const apply = (prev: PublicProfileForm) => ({
        ...prev,
        isPublicPublished: Boolean(updated.isPublicPublished),
        slug: updated.slug || prev.slug,
      });
      setForm(apply);
      setSavedForm(apply);
      setSuccess(next ? 'Wizytówka została opublikowana.' : 'Wizytówka została ukryta.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się zmienić statusu publikacji.');
    } finally {
      setPublishing(false);
    }
  };

  const toggleServiceVisibility = async (service: Service) => {
    setServiceSavingId(service.id);
    setError('');
    try {
      const updated = await serviceApi.update(service.id, {
        isPublicVisible: service.isPublicVisible === false,
      });
      setServices((prev) => prev.map((s) => (s.id === service.id ? updated : s)));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się zaktualizować widoczności usługi.');
    } finally {
      setServiceSavingId(null);
    }
  };

  const updateServicePublicDescription = async (service: Service, publicDescription: string) => {
    setServiceSavingId(service.id);
    setError('');
    try {
      const updated = await serviceApi.update(service.id, { publicDescription });
      setServices((prev) => prev.map((s) => (s.id === service.id ? updated : s)));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się zapisać opisu usługi.');
    } finally {
      setServiceSavingId(null);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('Link skopiowany do schowka.');
    } catch {
      setError('Nie udało się skopiować linku — zaznacz go i skopiuj ręcznie.');
    }
  };

  const setField = <K extends keyof PublicProfileForm>(key: K, value: PublicProfileForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const header = (children?: React.ReactNode, withActions = false) => (
    <PageHeader
      breadcrumbs={[{ label: 'Ustawienia salonu' }]}
      title="Wizytówka WWW"
      description="Publiczna strona salonu z ofertą, cennikiem, oddziałami i rezerwacją online."
      actions={
        withActions ? (
          <>
            {publicUrl && (
              <Button variant="outlined" startIcon={<OpenInNewIcon />} href={publicUrl} target="_blank" rel="noopener noreferrer">
                Podgląd
              </Button>
            )}
            {form.isPublicPublished ? (
              <Button variant="outlined" startIcon={<UnpublishedIcon />} onClick={togglePublished} disabled={publishing}>
                {publishing ? 'Zapisywanie…' : 'Wycofaj publikację'}
              </Button>
            ) : (
              <Button variant="contained" startIcon={<PublicIcon />} onClick={togglePublished} disabled={publishing}>
                {publishing ? 'Zapisywanie…' : 'Opublikuj'}
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      {children}
    </PageHeader>
  );

  if (loading) {
    return (
      <Box>
        {header()}
        <LoadingState variant="block" height={360} />
      </Box>
    );
  }

  if (!activeSalonId) {
    return (
      <Box>
        {header()}
        <Paper>
          <EmptyState icon={<StorefrontOutlinedIcon />} title="Nie wybrano salonu" description="Wybierz aktywny salon w górnym pasku, aby skonfigurować wizytówkę." />
        </Paper>
      </Box>
    );
  }

  const publicationPill = (
    <StatusPill label={form.isPublicPublished ? 'Opublikowana' : 'Nieopublikowana'} tone={form.isPublicPublished ? 'success' : 'neutral'} />
  );

  return (
    <Box>
      {header(
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ borderBottom: `1px solid ${tokens.line}` }}
        >
          <Tab icon={<StorefrontOutlinedIcon />} iconPosition="start" label="Profil" />
          <Tab icon={<PriceCheckIcon />} iconPosition="start" label="Oferta i cennik" />
          <Tab icon={<LocationCityIcon />} iconPosition="start" label="Oddziały" />
          <Tab icon={<EventAvailableIcon />} iconPosition="start" label="Rezerwacje" />
        </Tabs>,
        true,
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {tab === 0 && (
        <Paper sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: { xs: 1, md: 1.5 } }}>
          <FormSection
            first
            title="Adres wizytówki"
            description="Link, który podajesz klientom — w mediach społecznościowych, na wizytówkach i w wiadomościach."
          >
            <Stack gap={2}>
              <TextField
                fullWidth
                label="Końcówka adresu"
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value.toLowerCase())}
                placeholder="np. beauty-studio-krakow"
                helperText="Tylko małe litery, cyfry i myślniki."
                sx={{ '& .MuiInputBase-inputAdornedStart': { pl: 0.25 } }}
                InputProps={wide ? {
                  startAdornment: <InputAdornment position="start" sx={{ mr: 0 }}>visitio.online/s/</InputAdornment>,
                } : undefined}
              />
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Twój link</Typography>
                  {publicationPill}
                </Stack>
                {publicUrl ? (
                  <LinkBox url={publicUrl} onCopy={copyLink} />
                ) : (
                  <Typography variant="body2" sx={{ color: tokens.muted }}>
                    Uzupełnij końcówkę adresu, aby wygenerować link.
                  </Typography>
                )}
                <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                  {form.isPublicPublished
                    ? 'Wizytówka jest widoczna dla klientów.'
                    : 'Wizytówka jest ukryta — kliknij „Opublikuj”, gdy będzie gotowa.'}
                  {dirty && form.slug !== savedForm.slug ? ' Nowy adres zacznie działać po zapisaniu zmian.' : ''}
                </Typography>
              </Box>
            </Stack>
          </FormSection>

          <FormSection title="Nagłówek i opis" description="Pierwsza sekcja, którą klienci zobaczą na stronie salonu.">
            <Stack gap={2}>
              <TextField
                fullWidth
                label="Tytuł publiczny"
                value={form.publicTitle}
                onChange={(e) => setField('publicTitle', e.target.value)}
                placeholder="Np. Visitio Beauty Studio Kraków"
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Opis publiczny"
                value={form.publicDescription}
                onChange={(e) => setField('publicDescription', e.target.value)}
                placeholder="Opisz salon, specjalizację i atmosferę."
              />
            </Stack>
          </FormSection>

          <FormSection title="Dane kontaktowe" description="Widoczne na wizytówce. Mogą się różnić od danych w Profilu biznesu.">
            <FieldGrid columns={2}>
              <TextField fullWidth label="Telefon publiczny" value={form.publicPhone} onChange={(e) => setField('publicPhone', e.target.value)} />
              <TextField fullWidth label="E-mail publiczny" value={form.publicEmail} onChange={(e) => setField('publicEmail', e.target.value)} />
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth label="Adres publiczny" value={form.publicAddress} onChange={(e) => setField('publicAddress', e.target.value)} />
              </Box>
            </FieldGrid>
          </FormSection>
        </Paper>
      )}

      {tab === 1 && (
        <SectionCard
          title="Oferta i cennik"
          subtitle="Wybierz usługi widoczne na wizytówce i uzupełnij opisy dla klientów. Ceny pochodzą z modułu Usługi."
          action={
            services.length > 0 ? (
              <StatusPill label={`${countLabel(publishedServicesCount, 'usługa', 'usługi', 'usług')} na wizytówce`} tone="plum" noDot />
            ) : undefined
          }
          padding="none"
        >
          {servicesLoading ? (
            <Box sx={{ p: 2 }}><LoadingState variant="table" rows={4} /></Box>
          ) : services.length === 0 ? (
            <EmptyState
              compact
              icon={<ContentCutOutlinedIcon />}
              title="Brak usług"
              description="Dodaj usługi w module Usługi i cennik — pojawią się tutaj do publikacji."
              actions={<Button variant="outlined" component={RouterLink} to="/salon/services">Przejdź do usług</Button>}
            />
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Usługa</TableCell>
                    <TableCell>Kategoria</TableCell>
                    <TableCell align="right">Cena</TableCell>
                    <TableCell align="right">Czas</TableCell>
                    <TableCell>Opis publiczny</TableCell>
                    <TableCell align="center">Na wizytówce</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service) => {
                    const hidden = service.isPublicVisible === false;
                    return (
                      <TableRow key={service.id} hover>
                        <TableCell sx={{ color: service.isActive ? tokens.ink : tokens.muted }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'inherit' }}>{service.name}</Typography>
                          {!service.isActive && <StatusPill label="Nieaktywna" tone="neutral" size="small" sx={{ mt: 0.5 }} />}
                        </TableCell>
                        <TableCell sx={{ color: service.isActive ? tokens.inkSoft : tokens.muted }}>{service.category || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(service.price)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(service.duration)}</TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            maxRows={2}
                            placeholder={service.description || 'Opis dla klientów…'}
                            defaultValue={service.publicDescription || ''}
                            disabled={serviceSavingId === service.id}
                            inputProps={{ 'aria-label': `Opis publiczny: ${service.name}` }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value !== (service.publicDescription || '')) {
                                updateServicePublicDescription(service, value);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={hidden ? 'Pokaż na wizytówce' : 'Ukryj z wizytówki'}>
                            <span>
                              <IconButton
                                onClick={() => toggleServiceVisibility(service)}
                                disabled={!service.isActive || serviceSavingId === service.id}
                                aria-label={hidden ? `Pokaż na wizytówce: ${service.name}` : `Ukryj z wizytówki: ${service.name}`}
                                sx={{ color: hidden ? tokens.subtle : tokens.plum }}
                              >
                                {hidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      )}

      {tab === 2 && (
        <SectionCard
          title="Oddziały"
          subtitle="Jeśli salon należy do marki z wieloma lokalizacjami, klienci zobaczą listę oddziałów na wizytówce. Każdy opublikowany oddział musi mieć własny adres."
          padding="none"
          action={brand ? <Button variant="outlined" size="small" component={RouterLink} to="/salon/brands">Zarządzaj marką</Button> : undefined}
        >
          {!brand ? (
            <EmptyState
              compact
              icon={<LocationCityIcon />}
              title="Salon nie jest przypisany do marki"
              description="Marki i oddziały utworzysz w module Marki i oddziały."
              actions={<Button variant="outlined" component={RouterLink} to="/salon/brands">Przejdź do marek i oddziałów</Button>}
            />
          ) : (
            <>
              <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}`, fontSize: '0.9375rem', color: tokens.inkSoft }}>
                Marka: <b>{brand.name}</b> · {brand.salons?.length || 0} {plural(brand.salons?.length || 0, 'oddział', 'oddziały', 'oddziałów')}
              </Box>
              <TableContainer>
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Oddział</TableCell>
                      <TableCell>Miasto</TableCell>
                      <TableCell>Adres</TableCell>
                      <TableCell>Wizytówka</TableCell>
                      <TableCell align="right">Edycja</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {brand.salons?.map((branch) => {
                      const isCurrent = branch.id === activeSalonId;
                      return (
                        <TableRow key={branch.id} selected={isCurrent}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography sx={{ fontWeight: isCurrent ? 700 : 500, fontSize: '0.9375rem' }}>{branch.name}</Typography>
                              {isCurrent && <StatusPill label="Aktywny" tone="plum" size="small" noDot />}
                            </Stack>
                          </TableCell>
                          <TableCell>{branch.city}</TableCell>
                          <TableCell>{branch.address}</TableCell>
                          <TableCell>
                            <StatusPill
                              label={isCurrent && form.isPublicPublished ? 'Opublikowana' : 'Do skonfigurowania'}
                              tone={isCurrent && form.isPublicPublished ? 'success' : 'neutral'}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ color: tokens.muted, fontSize: '0.875rem' }}>
                            {isCurrent ? 'Edytujesz teraz' : 'Przełącz salon w górnym pasku'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </SectionCard>
      )}

      {tab === 3 && (
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
          <SectionCard
            title="Rezerwacje online"
            subtitle="Klienci rezerwują wizyty bezpośrednio z wizytówki. Wymaga godzin otwarcia i grafiku pracowników."
          >
            <Stack gap={1.5}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={form.allowOnlineBooking}
                    onChange={(e) => setField('allowOnlineBooking', e.target.checked)}
                  />
                }
                label="Włącz rezerwację online na wizytówce"
              />
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={form.allowPhoneContact}
                    onChange={(e) => setField('allowPhoneContact', e.target.checked)}
                  />
                }
                label="Pokaż przycisk kontaktu telefonicznego"
              />
              <Typography variant="body2" sx={{ color: tokens.muted }}>
                Po zapisaniu zmian klienci zobaczą kalendarz rezerwacji na stronie publicznej.
              </Typography>
              {form.allowOnlineBooking && publicUrl && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>Link do rezerwacji</Typography>
                  <LinkBox url={`${publicUrl}/rezerwacja`} onCopy={copyLink} />
                </Box>
              )}
            </Stack>
          </SectionCard>

          <SectionCard title="Godziny otwarcia" subtitle="Ustawiane w Profilu biznesu.">
            {workingHours.length > 0 ? (
              <Stack>
                {workingHours.map((item, i) => (
                  <Stack
                    key={item.id}
                    direction="row"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ py: 0.9, borderTop: i === 0 ? 'none' : `1px solid ${tokens.line}` }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{DAY_LABELS[item.dayOfWeek]}</Typography>
                    <Typography variant="body2" sx={{ color: item.isClosed ? tokens.muted : tokens.ink, fontVariantNumeric: 'tabular-nums' }}>
                      {item.isClosed ? 'Zamknięte' : `${item.openTime} – ${item.closeTime}`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Alert severity="warning">
                Brak godzin otwarcia — rezerwacja online nie będzie działać.
              </Alert>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Button component={RouterLink} to="/salon/profile" variant="outlined">
                Ustaw godziny otwarcia
              </Button>
              <Button component={RouterLink} to="/salon/staff" variant="text">
                Grafik pracowników
              </Button>
            </Stack>
          </SectionCard>
        </Box>
      )}

      <SaveBar
        open={dirty}
        onSave={handleSave}
        onDiscard={discard}
        saving={saving}
        message={saveError ? `Nie udało się zapisać: ${saveError}` : undefined}
      />

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};
